// Projects shown as "stories": one card at a time, with progress bars on top.
// - Auto-advances every STORY_MS while the projects slide is on screen.
// - Tap the left third of the card to go back, the rest to go forward,
//   swipe sideways to change project, press and hold to pause.
// - Going forward from the last project (or back from the first) moves on to
//   the next (or previous) slide; auto-advance just loops through the projects.
// - Each card shows an animated SVG diagram of the project; only the visible
//   card's animation runs, and it restarts when the card is shown again.
const STORY_MS = 9000;
const HOLD_MS = 220;
const SWIPE_PX = 40;
const MOVE_TOLERANCE = 10;

export default function initStories() {
  const root = document.getElementById("stories");
  if (!root) return;
  const slide = root.closest(".slide");
  const frame = root.querySelector(".stories__frame");
  const stories = [...root.querySelectorAll(".story")];
  // The animated diagram of each card (SMIL animations)
  const diagrams = stories.map((s) => s.querySelector("svg"));
  diagrams.forEach((svg) => svg?.pauseAnimations());
  const bars = [...root.querySelectorAll(".stories__bar i")];
  const pickers = [...document.querySelectorAll("[data-story]")];
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let index = 0;
  let elapsed = 0;
  let last = 0;
  let raf = 0;
  let visible = false;
  let paused = false;
  let leftFromEnd = false;
  const seen = new Set();

  function show(i) {
    const prev = index;
    index = (i + stories.length) % stories.length;
    elapsed = 0;
    seen.add(index);
    stories.forEach((story, k) => {
      const active = k === index;
      story.classList.toggle("is-active", active);
      story.classList.toggle("is-before", k < index);
      story.classList.toggle("is-after", k > index);
      story.setAttribute("aria-hidden", String(!active));
      story.inert = !active;
    });
    if (prev !== index) diagrams[prev]?.pauseAnimations();
    diagrams[index]?.setCurrentTime(0);
    playCurrent();
    pickers.forEach((p, k) => {
      p.classList.toggle("is-active", k === index);
      p.classList.toggle("is-seen", seen.has(k) && k !== index);
      p.setAttribute("aria-current", String(k === index));
    });
    paint();
  }

  // A move made by the visitor (tap, swipe, arrows, keys)
  function step(dir) {
    const target = index + dir;
    if (target < 0 || target >= stories.length) {
      leftFromEnd = target >= stories.length;
      document.getElementById(dir > 0 ? "next" : "prev").click();
      return;
    }
    show(target);
  }

  function playCurrent() {
    if (visible && !paused) diagrams[index]?.unpauseAnimations();
  }

  function paint() {
    const done = reduceMotion ? 1 : Math.min(elapsed / STORY_MS, 1);
    bars.forEach((bar, k) => {
      bar.style.transform = `scaleX(${k < index ? 1 : k > index ? 0 : done})`;
    });
    pickers.forEach((p, k) => p.style.setProperty("--p", k === index ? done : 0));
  }

  function tick(time) {
    raf = requestAnimationFrame(tick);
    const dt = last ? time - last : 0;
    last = time;
    if (!paused && !reduceMotion) {
      elapsed += dt;
      if (elapsed >= STORY_MS) show(index + 1);
    }
    paint();
  }

  function setVisible(on) {
    if (on === visible) return;
    visible = on;
    if (on) {
      last = 0;
      raf = requestAnimationFrame(tick);
      // came back after finishing the stories: start them over
      if (leftFromEnd) {
        leftFromEnd = false;
        show(0);
      }
      playCurrent();
    } else {
      cancelAnimationFrame(raf);
      diagrams[index]?.pauseAnimations();
    }
  }

  function setPaused(on) {
    paused = on;
    root.classList.toggle("is-paused", on);
    if (on) diagrams[index]?.pauseAnimations();
    else playCurrent();
  }

  const sync = () => setVisible(slide.classList.contains("is-active") && !document.hidden);
  document.addEventListener("slidechange", sync);
  document.addEventListener("visibilitychange", sync);

  // Tap / hold / swipe on the card
  let down = null;
  let holdTimer;
  frame.addEventListener("pointerdown", (e) => {
    if (e.button !== 0 || e.target.closest("a, button")) return;
    down = { x: e.clientX, y: e.clientY, held: false };
    holdTimer = setTimeout(() => {
      down.held = true;
      setPaused(true);
    }, HOLD_MS);
  });
  frame.addEventListener("pointermove", (e) => {
    if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > MOVE_TOLERANCE) {
      clearTimeout(holdTimer);
    }
  });
  frame.addEventListener("pointerup", (e) => {
    clearTimeout(holdTimer);
    if (!down) return;
    const { x, y, held } = down;
    down = null;
    if (held) {
      setPaused(false);
      return;
    }
    const dx = e.clientX - x;
    const dy = e.clientY - y;
    if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
      step(dx < 0 ? 1 : -1);
    } else if (Math.hypot(dx, dy) <= MOVE_TOLERANCE) {
      const r = frame.getBoundingClientRect();
      step(e.clientX - r.left < r.width / 3 ? -1 : 1);
    }
  });
  const cancel = () => {
    clearTimeout(holdTimer);
    if (down?.held) setPaused(false);
    down = null;
  };
  frame.addEventListener("pointercancel", cancel);
  frame.addEventListener("pointerleave", cancel);
  frame.addEventListener("contextmenu", (e) => e.preventDefault());
  // Keep swipes on the card from also changing the page's slide
  ["touchstart", "touchend"].forEach((type) =>
    frame.addEventListener(type, (e) => e.stopPropagation(), { passive: true })
  );

  root.querySelectorAll("[data-step]").forEach((btn) =>
    btn.addEventListener("click", () => step(Number(btn.dataset.step)))
  );
  pickers.forEach((p) => p.addEventListener("click", () => show(Number(p.dataset.story))));

  // While the carousel has focus, arrows move between projects instead of slides
  root.addEventListener("keydown", (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.stopPropagation();
      step(e.key === "ArrowRight" ? 1 : -1);
    } else if (e.key === " " && e.target === root) {
      e.preventDefault();
      setPaused(!paused);
    }
  });

  show(0);
}
